<template>
  <div class="test-choice-card">
    <!-- Corner decorations -->
    <div class="deco deco--tl">
      <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
        <circle cx="0" cy="0" r="80" fill="#EBF3FF" opacity="0.7"/>
        <circle cx="20" cy="20" r="30" fill="#F0D25B" opacity="0.15"/>
      </svg>
    </div>
    <div class="deco deco--br">
      <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
        <circle cx="120" cy="120" r="65" fill="#F0D25B" opacity="0.13"/>
        <circle cx="148" cy="148" r="28" fill="#DF654D" opacity="0.10"/>
      </svg>
    </div>

    <div class="card-content">
      <!-- Abby Header -->
      <div class="abby-header">
        <div class="abby-mascot">
          <img src="@/assets/pcu/2026/abby-smile-half.png" alt="Abby Mascot" class="abby-img"/>
        </div>
        <div class="abby-dialog">
          <template v-if="totalSelected === 0">
            <p>Hai <strong>{{ nickname || 'kamu' }}</strong>, tampaknya kamu belum punya pilihan spesifik.? Tenang, itu wajar banget! 😊</p>
            <p>Yuk ikut <strong>test singkat</strong> bareng Abby, hanya dengan 30 soal pilihan yang simpel dan cepat — biar ketahuan jurusan apa yang paling cocok sama <strong>bakat & minat</strong> kamu. 🎯</p>
          </template>
          <template v-else>
            <p>Wah, <strong>{{ nickname || 'kamu' }}</strong> sudah punya <strong>{{ totalSelected }} pilihan jurusan</strong>! 🌟</p>
            <p>Supaya rekomendasinya makin akurat dan personal, Abby saranin kamu ikut <strong>test</strong> dulu ya. Biar kamu bisa dapat rekomendasi jurusan yang pas sama dirimu! ✨</p>
          </template>
        </div>
      </div>

      <!-- Choice cards -->
      <div class="choice-section">
        <button class="choice-card take-test" @click="$emit('take')">
          <div class="choice-icon-wrap take-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
          </div>
          <div class="choice-text">
            <span class="choice-title">Ikuti Test ✏️</span>
            <span class="choice-desc">Jawab beberapa pertanyaan singkat untuk rekomendasi jurusan yang lebih personal & akurat</span>
          </div>
          <div class="choice-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </button>

        <button class="choice-card skip-test" @click="showConfirm = true">
          <div class="choice-icon-wrap skip-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
          </div>
          <div class="choice-text">
            <span class="choice-title">Lewati Test ⚡</span>
            <span class="choice-desc">Langsung proses hasil dari pilihan jurusan yang sudah kamu isi</span>
          </div>
          <div class="choice-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </button>
      </div>

      <!-- Back button -->
      <button class="btn-back" @click="$emit('back')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Kembali
      </button>
    </div>

    <!-- Confirmation overlay via Teleport -->
    <Teleport to="body">
      <Transition name="confirm-fade">
        <div v-if="showConfirm" class="confirm-overlay" @click.self="showConfirm = false">
          <div class="confirm-box">
            <div class="confirm-emoji">⚡</div>
            <h3 class="confirm-title">Lewati Test?</h3>
            <p class="confirm-desc">
              Hasil kamu akan langsung diproses dari pilihan jurusan sebelumnya tanpa melalui test.
            </p>
            <div class="confirm-btns">
              <button class="btn-cancel" @click="showConfirm = false">Batalkan</button>
              <button class="btn-confirm-yes" @click="handleConfirmSkip">Ya, Proses Sekarang</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  nickname: {
    type: String,
    default: ''
  },
  selectedProdis: {
    type: Array,
    default: () => []
  },
  extraProdis: {
    type: Array,
    default: () => []
  }
});

const totalSelected = computed(() => props.selectedProdis.length + props.extraProdis.length);

const emit = defineEmits(['take', 'skip', 'back']);

const showConfirm = ref(false);

function handleConfirmSkip() {
  showConfirm.value = false;
  emit('skip');
}
</script>

<style scoped>
.test-choice-card {
  background: #ffffff;
  border-radius: 28px;
  padding: 2.5rem;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.7);
  text-align: center;
  animation: card-in 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) both;
}

@keyframes card-in {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Corner decorations */
.deco {
  position: absolute;
  pointer-events: none;
  z-index: 0;
}
.deco--tl { top: -40px; left: -40px; }
.deco--br { bottom: -40px; right: -40px; }

.card-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

/* ---------- Abby Header ---------- */
.abby-header {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 12px;
  margin-bottom: 1.75rem;
  width: 100%;
}

.abby-mascot {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.abby-img {
  width: 190px;
  object-fit: contain;
  animation: float-abby 3s ease-in-out infinite;
  -webkit-mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
  display: block;
}

@keyframes float-abby {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-6px); }
}

/* Speech bubble */
.abby-dialog {
  background: #EFF6FF;
  border: 2px solid #26488A;
  border-radius: 16px 16px 16px 0;
  padding: 0.75rem 1rem;
  max-width: 260px;
  text-align: left;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #1e3a6e;
  box-shadow: 4px 4px 0 #26488A;
  animation: pop-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both;
  position: relative;
}

.abby-dialog p {
  margin: 0;
}
.abby-dialog p + p {
  margin-top: 0.35rem;
}

/* Tail outer */
.abby-dialog::before {
  content: '';
  position: absolute;
  bottom: -18px;
  left: -2px;
  border: 8px solid transparent;
  border-top-color: #26488A;
  border-bottom: none;
  border-left: none;
}

/* Tail inner */
.abby-dialog::after {
  content: '';
  position: absolute;
  bottom: -13px;
  left: 0;
  border: 7px solid transparent;
  border-top-color: #EFF6FF;
  border-bottom: none;
  border-left: none;
}

@keyframes pop-in {
  from { opacity: 0; transform: scale(0.85) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

/* ---------- Choice Cards ---------- */
.choice-section {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
  margin-bottom: 1.5rem;
}

.choice-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-radius: 16px;
  cursor: pointer;
  text-align: left;
  transition: transform 0.18s, box-shadow 0.18s;
  width: 100%;
  border: none;
  font-family: inherit;
}

.choice-card:active {
  transform: scale(0.97);
}

/* Take test — primary */
.take-test {
  background: linear-gradient(135deg, #26488A 0%, #1e3a6e 100%);
  color: #ffffff;
  box-shadow: 0 6px 20px rgba(38, 72, 138, 0.35);
}
.take-test:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(38, 72, 138, 0.45);
}

/* Skip test — outlined */
.skip-test {
  background: #ffffff;
  color: #26488A;
  border: 2px solid #d0dff5;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}
.skip-test:hover {
  border-color: #26488A;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(38, 72, 138, 0.12);
}

/* Icon wrap */
.choice-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.choice-icon-wrap svg {
  width: 20px;
  height: 20px;
}
.take-icon {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}
.skip-icon {
  background: #EFF6FF;
  color: #26488A;
}

.choice-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.choice-title {
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
}
.choice-desc {
  font-size: 0.78rem;
  opacity: 0.82;
  line-height: 1.4;
}

.choice-arrow {
  flex-shrink: 0;
  opacity: 0.6;
}
.choice-arrow svg {
  width: 18px;
  height: 18px;
  stroke-width: 2.5px;
}

/* ---------- Back Button ---------- */
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #8a99b3;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;
  font-family: inherit;
}
.btn-back svg {
  width: 16px;
  height: 16px;
  stroke-width: 2.5px;
}
.btn-back:hover {
  color: #26488A;
  background: #EFF6FF;
}

/* ---------- Confirm Overlay ---------- */
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.5rem;
}

.confirm-box {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem 2rem 1.5rem;
  max-width: 360px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: box-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes box-in {
  from { opacity: 0; transform: scale(0.9) translateY(16px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

.confirm-emoji {
  font-size: 2.5rem;
  line-height: 1;
  margin-bottom: 0.75rem;
}

.confirm-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1e3a6e;
  margin: 0 0 0.65rem;
}

.confirm-desc {
  font-size: 0.875rem;
  color: #556;
  line-height: 1.55;
  margin: 0 0 1.5rem;
}

.confirm-btns {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.btn-cancel {
  flex: 1;
  padding: 0.7rem 1rem;
  border-radius: 12px;
  border: 2px solid #d0dff5;
  background: #ffffff;
  color: #26488A;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s;
  font-family: inherit;
}
.btn-cancel:hover { border-color: #26488A; }

.btn-confirm-yes {
  flex: 1;
  padding: 0.7rem 1rem;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #26488A 0%, #1e3a6e 100%);
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(38, 72, 138, 0.35);
  transition: box-shadow 0.2s, transform 0.15s;
  font-family: inherit;
}
.btn-confirm-yes:hover {
  transform: translateY(-1px);
  box-shadow: 0 7px 20px rgba(38, 72, 138, 0.45);
}
.btn-confirm-yes:active { transform: scale(0.97); }

/* Confirm transition */
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}
.confirm-fade-enter-active .confirm-box,
.confirm-fade-leave-active .confirm-box {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}
.confirm-fade-enter-from .confirm-box,
.confirm-fade-leave-to .confirm-box {
  transform: scale(0.92) translateY(12px);
  opacity: 0;
}

/* ---------- Responsive ---------- */
@media (max-width: 480px) {
  .test-choice-card {
    padding: 1.75rem 1.25rem;
  }
  .abby-dialog {
    font-size: 0.82rem;
    max-width: 210px;
  }
  .abby-img { width: 150px; }
  .choice-title { font-size: 0.9rem; }
  .choice-desc  { font-size: 0.75rem; }
}
</style>
